import { BottomSheet } from '@s4l/ds'
import { useBrowser } from '@s4l/utils/hooks/useBrowser'
import cn from 'classnames'
import { getFilters } from 'client/components/FiltersContainer/selectors'
import { clearPropertyAction } from 'client/contexts/Property/actions'
import IconSearch from 'client/icons/Search'
import { getSearchParamsLocalStorage, setSearchParamsLocalStorage } from 'lib/storage'
import { buildLocation } from 'lib/utils/page'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import Button from 'src/comps/Atom/Buttons/Button'
import { DateSelectorConnected } from 'src/comps/Molecules/DateSelectorConnected'
import { PaxesConnected } from 'src/comps/Molecules/Paxes/PaxesConnected'
import MobilePill from 'src/comps/Molecules/Suggester/MobilePill'
import SuggesterConnected from 'src/comps/Molecules/SuggesterConnected'
import { getBaseParams } from 'src/comps/Page/selectors'
import { routes } from 'src/constants/routes'
import { DEFAULT_SEARCH } from 'src/constants/search'
import { isProperty } from 'src/context/search/utils'
import { updateSearchAction } from 'src/effects/Search/actions'
import { getDates, getSearch, getSearchPaxes } from 'src/effects/Search/selectors'
import { useKafkaEvent } from 'src/hooks/useKafkaEvent'
import { usePage } from 'src/hooks/usePage'
import { addError } from 'src/utils/clientErrorReporter'
import { SearchMode } from '../../../src/types/search'
import messages from './messages'
import styles from './styles.module.css'

interface Props {
  initSearch?: {
    city?: string
    country: string
    latitude: number
    longitude: number
    slug: string
    type: string
    description: string
    text?: string
    searchTerm?: string
  }
  onSubmit: () => void
  onInput?: () => void
  detail?: boolean
  disabled?: boolean
  stacked?: boolean
  mode?: SearchMode
  error?: boolean
}

const SearchForm = (props: Props) => {
  const {
    initSearch,
    onSubmit,
    detail = false,
    stacked = false,
    mode = SearchMode.Borderless,
    error,
    ...forwardProps
  } = props

  const { checkIn, checkOut } = useSelector(getDates)
  const searchParams = useSelector(getSearch)
  const { adults, children } = searchParams
  const paxes = { adults, children }

  return (
    <form
      className={cn('searchForm', styles.searchForm, {
        [styles.stacked]: stacked,
      })}
      data-testid="searchform-form"
      {...forwardProps}
    >
      <SuggesterConnected
        mode={mode}
        className="suggester-wrapper"
        error={error}
        initialValue={
          initSearch && {
            ID: '',
            Coordinate: {
              lat: initSearch?.latitude,
              lon: initSearch?.longitude,
            },
            Country: initSearch?.country,
            Slug: initSearch?.slug,
            Type: initSearch?.type,
            Text: initSearch?.text ?? '',
            Description: initSearch?.description ?? '',
            Stars: 0,
            CitySlug: '',
          }
        }
      />

      <DateSelectorConnected checkIn={checkIn} checkOut={checkOut} mode={mode} />

      <PaxesConnected initialPaxes={paxes} mode={mode} />

      <Button
        as="button"
        size="m"
        className={styles.buttonSearch}
        backgroundColor="brand1"
        borderColor="brand1"
        color="grey8"
        type="submit"
        leftIcon={<IconSearch size={20} fill="white" />}
        fullWidth
        data-testid="search-button"
        data-track-evt={detail ? 'search_button_click' : ''}
        onClick={onSubmit}
      >
        <FormattedMessage {...messages.search}>
          {(...content) => <span className={styles.buttonText}>{content}</span>}
        </FormattedMessage>
      </Button>
    </form>
  )
}

const Wrapper = ({ initSearch, onSubmit, mode = SearchMode.Full, ...props }: Props) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const router = useRouter()

  const { device } = useBrowser()

  const isMobile = device === 'mobile'
  const isTablet = device === 'tablet'

  const { language, market } = useSelector(getBaseParams)

  const searchParams = useSelector(getSearch)
  const filters = useSelector(getFilters)
  const { checkIn, checkOut } = useSelector(getDates)
  const paxes = useSelector(getSearchPaxes)
  const { pageId } = usePage()

  const { sendEvent: sendEventOpenSearch } = useKafkaEvent({
    page: `${pageId}`,
    eventName: 'open_search',
  })

  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false)

  const location = {
    country: initSearch?.country ?? '',
    latitude: initSearch?.latitude ?? 0,
    longitude: initSearch?.longitude ?? 0,
    slug: initSearch?.slug ?? '',
    type: initSearch?.type ?? '',
    description: initSearch?.description ?? '',
    text: initSearch?.text ?? '',
    searchTerm: initSearch?.description ?? '',
  }

  const [search, setSearch] = useState(location)
  const [oldSearch, setOldSearch] = useState(location)
  const [error, setError] = useState(false)

  useEffect(() => {
    dispatch(
      updateSearchAction({
        ...location,
        term: location.searchTerm,
      })
    )
  }, []) //eslint-disable-line

  useEffect(() => {
    setSearch(searchParams)
  }, [searchParams])

  function saveSearchToLocalStorage(updatedParams: Record<string, string>) {
    const searchStored = getSearchParamsLocalStorage()

    setSearchParamsLocalStorage({
      ...searchStored,
      ...updatedParams,
    })
  }

  function redirectTo(params: Record<string, string>) {
    const isPropertyResult = isProperty(params.type)

    const isMap = router.asPath.includes('map')
    const isPropertyMap = router.asPath.includes('map') && router.asPath.includes('hotel')

    if (isPropertyMap) {
      return routes.map.page
    }

    if (params.type === 'zone') {
      return isMap ? routes.map.page : routes.list.page
    }

    if (isPropertyResult) {
      return routes.detail.page
    }

    if (isMap) {
      return routes.map.page
    }

    return routes.list.page
  }

  async function handleSubmitForm(event?: React.MouseEvent) {
    event?.preventDefault()
    if (!searchParams.term) {
      setError(true)
      return
    }

    const params = {
      city: searchParams.city,
      country: searchParams.country,
      slug: searchParams.slug,
      type: searchParams.type === 'province_state' ? 'zone' : searchParams.type, // TODO: remove this when API returns the right type !!
    }

    if (isProperty(params.type)) {
      params.slug = [searchParams.slug, searchParams.city].filter(Boolean).join('_')
    }

    const query = {
      ...router.query,
      ...searchParams,
      ...params,
      lang: language,
      market,
    }

    delete query.highlightedProperty

    const updatedParams = {
      ...DEFAULT_SEARCH,
      ...searchParams,
      filters,
    }

    dispatch(clearPropertyAction(updatedParams))
    saveSearchToLocalStorage(updatedParams)

    const target = buildLocation({
      page: redirectTo(params),
      path: params,
      query,
    })

    if (target.as !== router.asPath.split('?')[0]) {
      const url = new URL(target.as, document.location.origin)
      const context = {
        ...params,
        query,
      }

      if (url.pathname === '/hotel/[country]/[slug]' || !target.as) {
        addError(new Error('[Suggester] Bad URL'), context)
      }

      await router.push(target.href)
    }

    if (target.as === router.asPath) {
      const targetForSameLocation = buildLocation({
        page: redirectTo(params),
        path: params,
        query,
        isSameLocation: true,
      })

      router.push(targetForSameLocation.href, targetForSameLocation.as)
    }

    onSubmit?.()
  }

  function onSubmitMobile(isOpen = false) {
    if (isOpen) {
      sendEventOpenSearch({
        section: 'omnisearch',
      })
      setOldSearch(search)
    }

    setIsBottomSheetOpen(isOpen)

    if (!isOpen) {
      if (!searchParams.term) {
        dispatch(updateSearchAction(oldSearch))
      }
      handleSubmitForm()
    }
  }

  let render = (
    <SearchForm
      {...props}
      initSearch={search}
      onSubmit={handleSubmitForm}
      error={error}
      onInput={() => setError(false)}
    />
  )

  const suggestionText = isProperty(search?.type) ? search?.text : search?.description

  if (isMobile) {
    render = (
      <BottomSheet open={isBottomSheetOpen} onOpenChange={onSubmitMobile} snapPoints={[]}>
        <MobilePill
          mode={mode}
          text={suggestionText}
          paxes={paxes}
          checkIn={checkIn}
          checkOut={checkOut}
        />

        <BottomSheet.Content title={intl.formatMessage({ id: 'modify.search' })}>
          <SearchForm
            stacked
            initSearch={search}
            mode={SearchMode.Full}
            error={error}
            onInput={() => setError(false)}
            onSubmit={() => onSubmitMobile()}
            {...props}
          />
        </BottomSheet.Content>
      </BottomSheet>
    )
  } else if (isTablet) {
    render = (
      <BottomSheet open={isBottomSheetOpen} onOpenChange={onSubmitMobile} snapPoints={[]}>
        {mode === SearchMode.Full && (
          <BottomSheet.Trigger className={styles.searchSummary}>
            <SearchForm
              disabled
              initSearch={search}
              {...props}
              onSubmit={() => ({})}
              error={error}
            />
          </BottomSheet.Trigger>
        )}

        {mode === SearchMode.Readonly && (
          <MobilePill
            mode={mode}
            text={suggestionText}
            paxes={paxes}
            checkIn={checkIn}
            checkOut={checkOut}
          />
        )}

        <BottomSheet.Content title={intl.formatMessage({ id: 'modify.search' })}>
          <SearchForm
            stacked
            initSearch={search}
            mode={SearchMode.Full}
            onSubmit={() => onSubmitMobile()}
            onInput={() => setError(false)}
            error={error}
            {...props}
          />
        </BottomSheet.Content>
      </BottomSheet>
    )
  }

  return render
}

export default Wrapper
